import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus,
    Edit,
    Trash,
    Search,
    Check,
    X,
    Filter,
    ChevronDown,
    ChevronRight,
    DollarSign,
    History,
    Link2
} from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import authUtils from '../utils/authUtils';
import GhepSoHdModal from '../payroll/components/GhepSoHdModal';
import { syncEmployeeTeams } from '../payroll/data/employeeTeams';

const EMPTY_PHAN_BO = {
    'ID TỔ CÔNG ĐOẠN': '',
    'TỔ': '',
    'MÃ CÔNG ĐOẠN': '',
    'TÊN CÔNG ĐOẠN': '',
    'NHÂN SỰ PHỤ TRÁCH': '',
    'SỐ HĐ PHỤ TRÁCH': '',
    'HIỆU LỰC TỪ': '',
    'HIỆU LỰC ĐẾN': '',
    'NHÓM': '',
    'ĐƠN VỊ TÍNH': '',
    'PP TÍNH NĂNG SUẤT': ''
};

const PhanBoNhanSuManagement = () => {
    // State Management
    const [phanBoNhanSus, setPhanBoNhanSus] = useState([]);
    const [congDoanDonGias, setCongDoanDonGias] = useState([]);
    const [danhSachNhanSu, setDanhSachNhanSu] = useState([]);
    const [hieuChinhList, setHieuChinhList] = useState([]);

    // State cho data đã được tính toán đơn giá
    const [phanBoWithDonGia, setPhanBoWithDonGia] = useState([]);

    const [currentPhanBoNhanSu, setCurrentPhanBoNhanSu] = useState(EMPTY_PHAN_BO);

    // State cho modal hiệu chỉnh
    const [showHieuChinhModal, setShowHieuChinhModal] = useState(false);
    const [currentHieuChinh, setCurrentHieuChinh] = useState({
        'NGÀY HIỆU CHỈNH': '',
        'HIỆU LỰC TỪ': '',
        'HIỆU LỰC ĐẾN': '',
        'ID HIỆU CHỈNH': '',
        'ID TỔ CÔNG ĐOẠN': '',
        'TỔ': '',
        'MÃ CÔNG ĐOẠN': '',
        'TÊN CÔNG ĐOẠN': '',
        'NHÂN SỰ PHỤ TRÁCH': '',
        'TIỀN HIỆU CHỈNH': '',
        'GHI CHÚ': ''
    });
    const [selectedItemForHieuChinh, setSelectedItemForHieuChinh] = useState(null);
    const [isEditHieuChinh, setIsEditHieuChinh] = useState(false);

    // State cho modal xem lịch sử hiệu chỉnh
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyItem, setHistoryItem] = useState(null);
    const [historyList, setHistoryList] = useState([]);

    const [showModal, setShowModal] = useState(false);
    const [showGhepSoHd, setShowGhepSoHd] = useState(false);
    const isAdmin = authUtils.getUserData()?.['Phân quyền'] === 'Admin';
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [search, setSearch] = useState('');
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [isEditMode, setIsEditMode] = useState(false);
    const [toFilter, setToFilter] = useState('');

    const [selectedNhanSu, setSelectedNhanSu] = useState('');

    // Expand/collapse
    const [expandedNhoms, setExpandedNhoms] = useState(new Set());
    const [expandedTos, setExpandedTos] = useState(new Set());

    // Search nhân sự
    const [searchNhanSu, setSearchNhanSu] = useState('');

    // Dropdown Tổ (multi-select khi thêm mới)
    const [showToDropdown, setShowToDropdown] = useState(false);
    const [searchTo, setSearchTo] = useState('');
    const [selectedTos, setSelectedTos] = useState([]); // Danh sách tổ được chọn (chỉ dùng khi thêm mới)

    // Ngày để tính đơn giá (mặc định là ngày hiện tại)
    const [ngayTinhDonGia, setNgayTinhDonGia] = useState(new Date().toISOString().split('T')[0]);

    // State cho xóa hiệu chỉnh
    const [showDeleteHieuChinhConfirm, setShowDeleteHieuChinhConfirm] = useState(false);
    const [hieuChinhToDelete, setHieuChinhToDelete] = useState(null);

    // ===================== FETCH DATA =====================

    const fetchPhanBoNhanSus = async () => {
        try {
            const response = await authUtils.apiRequest('TO_PBNS', 'Find', {});
            setPhanBoNhanSus(response);
        } catch (error) {
            console.error('Error fetching phan bo nhan su list:', error);
            toast.error('Lỗi khi tải danh sách phân bổ nhân sự');
        }
    };

    const fetchCongDoanDonGias = async () => {
        try {
            const response = await authUtils.apiRequest('CONGDOAN_DONGIA', 'Find', {});
            setCongDoanDonGias(response);
        } catch (error) {
            console.error('Error fetching cong doan don gia list:', error);
            toast.error('Lỗi khi tải danh sách công đoạn đơn giá');
        }
    };

    const fetchDanhSachNhanSu = async () => {
        try {
            const response = await authUtils.apiRequest('NHANSU', 'Find', {});
            setDanhSachNhanSu(response);
        } catch (error) {
            console.error('Error fetching danh sach nhan su:', error);
            toast.error('Lỗi khi tải danh sách nhân sự');
        }
    };

    const fetchHieuChinhList = async () => {
        try {
            const response = await authUtils.apiRequest('HIEU_CHINH_TIEN_NANG_SUAT', 'Find', {});
            setHieuChinhList(response);
        } catch (error) {
            console.error('Error fetching hieu chinh list:', error);
            toast.error('Lỗi khi tải danh sách hiệu chỉnh');
        }
    };

    useEffect(() => {
        fetchPhanBoNhanSus();
        fetchCongDoanDonGias();
        fetchDanhSachNhanSu();
        fetchHieuChinhList();
    }, []);

    // ===================== TÍNH ĐƠN GIÁ ẢO =====================

    // Hàm lấy đơn giá theo mã công đoạn và ngày
    const getDonGiaByMaCongDoanAndDate = useCallback((maCongDoan, ngay) => {
        if (!maCongDoan || !ngay) return null;

        const targetDate = new Date(ngay);

        const matchingRecords = congDoanDonGias.filter(cd => cd['MÃ CÔNG ĐOẠN'] === maCongDoan);

        if (matchingRecords.length === 0) return null;

        const validRecord = matchingRecords.find(cd => {
            const hieuLucTu = cd['HIỆU LỰC TỪ'] ? new Date(cd['HIỆU LỰC TỪ']) : null;
            const hieuLucDen = cd['HIỆU LỰC ĐẾN'] ? new Date(cd['HIỆU LỰC ĐẾN']) : null;

            const isAfterStart = !hieuLucTu || targetDate >= hieuLucTu;
            const isBeforeEnd = !hieuLucDen || targetDate <= hieuLucDen;

            return isAfterStart && isBeforeEnd;
        });

        if (validRecord) {
            return {
                donGia: validRecord['ĐƠN GIÁ NĂNG SUẤT'],
                hieuLucTu: validRecord['HIỆU LỰC TỪ'],
                hieuLucDen: validRecord['HIỆU LỰC ĐẾN'],
                idCaiDat: validRecord['ID CÀI ĐẶT']
            };
        }

        const sortedRecords = matchingRecords
            .filter(cd => cd['HIỆU LỰC TỪ'])
            .sort((a, b) => new Date(b['HIỆU LỰC TỪ']) - new Date(a['HIỆU LỰC TỪ']));

        if (sortedRecords.length > 0) {
            const latestRecord = sortedRecords[0];
            return {
                donGia: latestRecord['ĐƠN GIÁ NĂNG SUẤT'],
                hieuLucTu: latestRecord['HIỆU LỰC TỪ'],
                hieuLucDen: latestRecord['HIỆU LỰC ĐẾN'],
                idCaiDat: latestRecord['ID CÀI ĐẶT'],
                isOutdated: true
            };
        }

        return null;
    }, [congDoanDonGias]);

    // Hàm lấy hiệu chỉnh theo ID TỔ CÔNG ĐOẠN và ngày
    const getHieuChinhByIdToCongDoanAndDate = useCallback((idToCongDoan, ngay) => {
        if (!idToCongDoan || !ngay) return null;

        const targetDate = new Date(ngay);

        const matchingRecords = hieuChinhList.filter(hc => hc['ID TỔ CÔNG ĐOẠN'] === idToCongDoan);

        if (matchingRecords.length === 0) return null;

        // Tìm hiệu chỉnh có hiệu lực tại ngày target
        const validRecord = matchingRecords.find(hc => {
            const hieuLucTu = hc['HIỆU LỰC TỪ'] ? new Date(hc['HIỆU LỰC TỪ']) : null;
            const hieuLucDen = hc['HIỆU LỰC ĐẾN'] ? new Date(hc['HIỆU LỰC ĐẾN']) : null;

            const isAfterStart = !hieuLucTu || targetDate >= hieuLucTu;
            const isBeforeEnd = !hieuLucDen || targetDate <= hieuLucDen;

            return isAfterStart && isBeforeEnd;
        });

        if (validRecord) {
            return {
                tienHieuChinh: parseFloat(validRecord['TIỀN HIỆU CHỈNH']) || 0,
                hieuLucTu: validRecord['HIỆU LỰC TỪ'],
                hieuLucDen: validRecord['HIỆU LỰC ĐẾN'],
                idHieuChinh: validRecord['ID HIỆU CHỈNH'],
                ghiChu: validRecord['GHI CHÚ']
            };
        }

        return null;
    }, [hieuChinhList]);

    // Tính toán data với đơn giá ảo và hiệu chỉnh
    useEffect(() => {
        if (phanBoNhanSus.length > 0) {
            const dataWithDonGia = phanBoNhanSus.map(item => {
                const donGiaInfo = getDonGiaByMaCongDoanAndDate(
                    item['MÃ CÔNG ĐOẠN'],
                    ngayTinhDonGia
                );

                const hieuChinhInfo = getHieuChinhByIdToCongDoanAndDate(
                    item['ID TỔ CÔNG ĐOẠN'],
                    ngayTinhDonGia
                );

                const donGiaGoc = parseFloat(donGiaInfo?.donGia) || 0;
                const tienHieuChinh = hieuChinhInfo?.tienHieuChinh || 0;
                const donGiaSauHieuChinh = donGiaGoc + tienHieuChinh;

                // Kiểm tra hiệu lực của bản phân bổ nhân sự tại ngày tính
                const targetDate = new Date(ngayTinhDonGia);
                const pbHieuLucTu = item['HIỆU LỰC TỪ'] ? new Date(item['HIỆU LỰC TỪ']) : null;
                const pbHieuLucDen = item['HIỆU LỰC ĐẾN'] ? new Date(item['HIỆU LỰC ĐẾN']) : null;
                const isPBActive = (!pbHieuLucTu || targetDate >= pbHieuLucTu) &&
                    (!pbHieuLucDen || targetDate <= pbHieuLucDen);

                return {
                    ...item,
                    'ĐƠN GIÁ NĂNG SUẤT': donGiaGoc,
                    'HIỆU LỰC TỪ_DONGIA': donGiaInfo?.hieuLucTu || '',
                    'HIỆU LỰC ĐẾN_DONGIA': donGiaInfo?.hieuLucDen || '',
                    'ID CÀI ĐẶT_DONGIA': donGiaInfo?.idCaiDat || '',
                    'IS_OUTDATED': donGiaInfo?.isOutdated || false,
                    'IS_PB_ACTIVE': isPBActive,
                    'TIỀN HIỆU CHỈNH': tienHieuChinh,
                    'ĐƠN GIÁ SAU HIỆU CHỈNH': donGiaSauHieuChinh,
                    'HIỆU LỰC TỪ_HC': hieuChinhInfo?.hieuLucTu || '',
                    'HIỆU LỰC ĐẾN_HC': hieuChinhInfo?.hieuLucDen || '',
                    'ID HIỆU CHỈNH': hieuChinhInfo?.idHieuChinh || '',
                    'GHI CHÚ_HC': hieuChinhInfo?.ghiChu || '',
                    'CÓ HIỆU CHỈNH': !!hieuChinhInfo
                };
            });

            setPhanBoWithDonGia(dataWithDonGia);
        } else {
            setPhanBoWithDonGia([]);
        }
    }, [phanBoNhanSus, congDoanDonGias, hieuChinhList, ngayTinhDonGia, getDonGiaByMaCongDoanAndDate, getHieuChinhByIdToCongDoanAndDate]);

    // ===================== UTILITIES =====================

    const generateIdToCongDoan = (to, maCongDoan, nhanSu = '') => {
        let id = `${to} ${maCongDoan}`;
        if (nhanSu) id += ` ${nhanSu}`;
        return id;
    };

    // Format date cho ID HIỆU CHỈNH (dd/mm/yyyy)
    const formatDateForId = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Generate ID HIỆU CHỈNH
    const generateIdHieuChinh = (idToCongDoan, hieuLucTu, hieuLucDen) => {
        if (!idToCongDoan || !hieuLucTu) return '';
        const tuFormatted = formatDateForId(hieuLucTu);
        const denFormatted = hieuLucDen ? formatDateForId(hieuLucDen) : '';
        return `HC-${idToCongDoan}(${tuFormatted}-${denFormatted})`;
    };

    const toggleNhom = (nhomName) => {
        const newExpanded = new Set(expandedNhoms);
        if (newExpanded.has(nhomName)) newExpanded.delete(nhomName);
        else newExpanded.add(nhomName);
        setExpandedNhoms(newExpanded);
    };

    const toggleTo = (nhomName, toName) => {
        const key = `${nhomName}-${toName}`;
        const newExpanded = new Set(expandedTos);
        if (newExpanded.has(key)) newExpanded.delete(key);
        else newExpanded.add(key);
        setExpandedTos(newExpanded);
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            return dateString;
        }
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // ===================== SORT & FILTER =====================

    const getSortedItems = useCallback(() => {
        const sortableItems = [...phanBoWithDonGia];
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                const keyA = a[sortConfig.key];
                const keyB = b[sortConfig.key];

                if (sortConfig.key === 'ĐƠN GIÁ NĂNG SUẤT' || sortConfig.key === 'ĐƠN GIÁ SAU HIỆU CHỈNH') {
                    const numA = parseFloat(keyA) || 0;
                    const numB = parseFloat(keyB) || 0;
                    if (numA < numB) return sortConfig.direction === 'ascending' ? -1 : 1;
                    if (numA > numB) return sortConfig.direction === 'ascending' ? 1 : -1;
                    return 0;
                } else {
                    if ((keyA || '') < (keyB || '')) return sortConfig.direction === 'ascending' ? -1 : 1;
                    if ((keyA || '') > (keyB || '')) return sortConfig.direction === 'ascending' ? 1 : -1;
                    return 0;
                }
            });
        }
        return sortableItems;
    }, [phanBoWithDonGia, sortConfig]);

    const filteredItems = getSortedItems().filter(item => {
        const nhanSuStr = item['NHÂN SỰ PHỤ TRÁCH'] || '';

        const matchesSearch =
            item['TỔ']?.toLowerCase().includes(search.toLowerCase()) ||
            item['TÊN CÔNG ĐOẠN']?.toLowerCase().includes(search.toLowerCase()) ||
            item['MÃ CÔNG ĐOẠN']?.toLowerCase().includes(search.toLowerCase()) ||
            item['NHÓM']?.toLowerCase().includes(search.toLowerCase()) ||
            nhanSuStr.toLowerCase().includes(search.toLowerCase());

        const matchesToFilter =
            !toFilter ||
            item['TỔ']?.toLowerCase().includes(toFilter.toLowerCase());

        return matchesSearch && matchesToFilter;
    });

    const groupDataByNhom = useCallback(() => {
        const grouped = {};

        filteredItems.forEach(item => {
            const nhom = item['NHÓM'] || 'Chưa phân nhóm';
            const to = item['TỔ'] || 'Chưa có tổ';

            if (!grouped[nhom]) grouped[nhom] = {};
            if (!grouped[nhom][to]) grouped[nhom][to] = [];

            grouped[nhom][to].push(item);
        });

        const sortedGroups = {};
        Object.keys(grouped)
            .sort()
            .forEach(nhomKey => {
                sortedGroups[nhomKey] = {};
                Object.keys(grouped[nhomKey])
                    .sort()
                    .forEach(toKey => {
                        sortedGroups[nhomKey][toKey] = grouped[nhomKey][toKey].sort((a, b) =>
                            (a['TÊN CÔNG ĐOẠN'] || '').localeCompare(b['TÊN CÔNG ĐOẠN'] || '')
                        );
                    });
            });

        return sortedGroups;
    }, [filteredItems]);

    const groupedData = groupDataByNhom();
    const uniqueTos = [...new Set(phanBoNhanSus.map(item => item['TỔ']).filter(Boolean))];

    const removeDiacritics = (str) => {
        if (!str) return '';
        return str
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/đ/g, 'd')
            .replace(/Đ/g, 'D');
    };

    const filteredNhanSu = React.useMemo(() => {
        if (!searchNhanSu.trim()) {
            return danhSachNhanSu;
        }

        const searchTerm = searchNhanSu.trim().toLowerCase();
        const searchNoDiacritics = removeDiacritics(searchTerm);

        return danhSachNhanSu.filter(nv => {
            const hoVaTen = nv['HỌ VÀ TÊN'] || '';
            const nameNoDiacritics = removeDiacritics(hoVaTen.toLowerCase());

            return nameNoDiacritics.includes(searchNoDiacritics) ||
                hoVaTen.toLowerCase().includes(searchTerm);
        });
    }, [searchNhanSu, danhSachNhanSu]);

    const uniqueCongDoans = React.useMemo(() => {
        const seen = new Map();
        congDoanDonGias.forEach(cd => {
            const maCongDoan = cd['MÃ CÔNG ĐOẠN'];
            if (!seen.has(maCongDoan)) {
                seen.set(maCongDoan, {
                    'MÃ CÔNG ĐOẠN': cd['MÃ CÔNG ĐOẠN'],
                    'TÊN CÔNG ĐOẠN': cd['TÊN CÔNG ĐOẠN'],
                    'NHÓM NĂNG SUẤT': cd['NHÓM NĂNG SUẤT'],
                    'ĐƠN VỊ TÍNH': cd['ĐƠN VỊ TÍNH'],
                    'PP TÍNH NĂNG SUẤT': cd['PP TÍNH NĂNG SUẤT']
                });
            }
        });
        return Array.from(seen.values());
    }, [congDoanDonGias]);

    // ===================== HIỆU CHỈNH HANDLERS =====================

    const handleOpenHieuChinhModal = (item, existingHieuChinh = null) => {
        setSelectedItemForHieuChinh(item);

        if (existingHieuChinh) {
            setIsEditHieuChinh(true);
            setCurrentHieuChinh({
                'NGÀY HIỆU CHỈNH': existingHieuChinh['NGÀY HIỆU CHỈNH'] || '',
                'HIỆU LỰC TỪ': existingHieuChinh['HIỆU LỰC TỪ'] || '',
                'HIỆU LỰC ĐẾN': existingHieuChinh['HIỆU LỰC ĐẾN'] || '',
                'ID HIỆU CHỈNH': existingHieuChinh['ID HIỆU CHỈNH'] || '',
                'ID TỔ CÔNG ĐOẠN': existingHieuChinh['ID TỔ CÔNG ĐOẠN'] || '',
                'TỔ': existingHieuChinh['TỔ'] || '',
                'MÃ CÔNG ĐOẠN': existingHieuChinh['MÃ CÔNG ĐOẠN'] || '',
                'TÊN CÔNG ĐOẠN': existingHieuChinh['TÊN CÔNG ĐOẠN'] || '',
                'NHÂN SỰ PHỤ TRÁCH': existingHieuChinh['NHÂN SỰ PHỤ TRÁCH'] || '',
                'TIỀN HIỆU CHỈNH': existingHieuChinh['TIỀN HIỆU CHỈNH'] || '',
                'GHI CHÚ': existingHieuChinh['GHI CHÚ'] || ''
            });
        } else {
            setIsEditHieuChinh(false);
            const today = new Date().toISOString().split('T')[0];
            setCurrentHieuChinh({
                'NGÀY HIỆU CHỈNH': today,
                'HIỆU LỰC TỪ': today,
                'HIỆU LỰC ĐẾN': '',
                'ID HIỆU CHỈNH': '',
                'ID TỔ CÔNG ĐOẠN': item['ID TỔ CÔNG ĐOẠN'],
                'TỔ': item['TỔ'],
                'MÃ CÔNG ĐOẠN': item['MÃ CÔNG ĐOẠN'],
                'TÊN CÔNG ĐOẠN': item['TÊN CÔNG ĐOẠN'],
                'NHÂN SỰ PHỤ TRÁCH': item['NHÂN SỰ PHỤ TRÁCH'],
                'TIỀN HIỆU CHỈNH': '',
                'GHI CHÚ': ''
            });
        }

        setShowHieuChinhModal(true);
    };

    const handleCloseHieuChinhModal = () => {
        setShowHieuChinhModal(false);
        setSelectedItemForHieuChinh(null);
        setIsEditHieuChinh(false);
        setCurrentHieuChinh({
            'NGÀY HIỆU CHỈNH': '',
            'HIỆU LỰC TỪ': '',
            'HIỆU LỰC ĐẾN': '',
            'ID HIỆU CHỈNH': '',
            'ID TỔ CÔNG ĐOẠN': '',
            'TỔ': '',
            'MÃ CÔNG ĐOẠN': '',
            'TÊN CÔNG ĐOẠN': '',
            'NHÂN SỰ PHỤ TRÁCH': '',
            'TIỀN HIỆU CHỈNH': '',
            'GHI CHÚ': ''
        });
    };

    const handleHieuChinhInputChange = (field, value) => {
        setCurrentHieuChinh(prev => {
            const updated = { ...prev, [field]: value };

            // Auto-generate ID HIỆU CHỈNH khi thay đổi hiệu lực
            if (field === 'HIỆU LỰC TỪ' || field === 'HIỆU LỰC ĐẾN') {
                const hieuLucTu = field === 'HIỆU LỰC TỪ' ? value : updated['HIỆU LỰC TỪ'];
                const hieuLucDen = field === 'HIỆU LỰC ĐẾN' ? value : updated['HIỆU LỰC ĐẾN'];

                if (updated['ID TỔ CÔNG ĐOẠN'] && hieuLucTu) {
                    updated['ID HIỆU CHỈNH'] = generateIdHieuChinh(
                        updated['ID TỔ CÔNG ĐOẠN'],
                        hieuLucTu,
                        hieuLucDen
                    );
                }
            }

            return updated;
        });
    };

    const validateHieuChinh = (hieuChinh) => {
        const errors = [];
        if (!hieuChinh['NGÀY HIỆU CHỈNH']) errors.push('Ngày hiệu chỉnh không được để trống');
        if (!hieuChinh['HIỆU LỰC TỪ']) errors.push('Hiệu lực từ không được để trống');
        if (!hieuChinh['TIỀN HIỆU CHỈNH'] && hieuChinh['TIỀN HIỆU CHỈNH'] !== 0) {
            errors.push('Tiền hiệu chỉnh không được để trống');
        }

        if (hieuChinh['HIỆU LỰC ĐẾN'] && hieuChinh['HIỆU LỰC TỪ']) {
            if (new Date(hieuChinh['HIỆU LỰC ĐẾN']) < new Date(hieuChinh['HIỆU LỰC TỪ'])) {
                errors.push('Hiệu lực đến phải sau hiệu lực từ');
            }
        }

        return errors;
    };

    const handleSaveHieuChinh = async () => {
        if (isSubmitting) return;

        try {
            setIsSubmitting(true);
            const errors = validateHieuChinh(currentHieuChinh);
            if (errors.length > 0) {
                toast.error(errors.join('\n'));
                setIsSubmitting(false);
                return;
            }

            // Đảm bảo ID HIỆU CHỈNH được tạo
            let dataToSave = { ...currentHieuChinh };
            if (!dataToSave['ID HIỆU CHỈNH']) {
                dataToSave['ID HIỆU CHỈNH'] = generateIdHieuChinh(
                    dataToSave['ID TỔ CÔNG ĐOẠN'],
                    dataToSave['HIỆU LỰC TỪ'],
                    dataToSave['HIỆU LỰC ĐẾN']
                );
            }

            if (isEditHieuChinh) {
                await authUtils.apiRequest('HIEU_CHINH_TIEN_NANG_SUAT', 'Edit', {
                    Rows: [dataToSave]
                });
                toast.success('Cập nhật hiệu chỉnh thành công!');
            } else {
                // Kiểm tra trùng ID
                const exists = hieuChinhList.some(
                    hc => hc['ID HIỆU CHỈNH'] === dataToSave['ID HIỆU CHỈNH']
                );

                if (exists) {
                    toast.error('ID hiệu chỉnh này đã tồn tại! Vui lòng kiểm tra lại thời gian hiệu lực.');
                    setIsSubmitting(false);
                    return;
                }

                await authUtils.apiRequest('HIEU_CHINH_TIEN_NANG_SUAT', 'Add', {
                    Rows: [dataToSave]
                });
                toast.success('Thêm hiệu chỉnh mới thành công!');
            }

            await fetchHieuChinhList();
            handleCloseHieuChinhModal();
        } catch (error) {
            console.error('Error saving hieu chinh:', error);
            toast.error('Có lỗi xảy ra: ' + (error.message || 'Không thể lưu hiệu chỉnh'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // ===================== HISTORY MODAL =====================

    const handleOpenHistoryModal = (item) => {
        setHistoryItem(item);
        const history = hieuChinhList.filter(hc => hc['ID TỔ CÔNG ĐOẠN'] === item['ID TỔ CÔNG ĐOẠN']);
        // Sắp xếp theo ngày hiệu lực từ mới nhất
        history.sort((a, b) => new Date(b['HIỆU LỰC TỪ']) - new Date(a['HIỆU LỰC TỪ']));
        setHistoryList(history);
        setShowHistoryModal(true);
    };

    const handleCloseHistoryModal = () => {
        setShowHistoryModal(false);
        setHistoryItem(null);
        setHistoryList([]);
    };

    // ===================== DELETE HIỆU CHỈNH =====================

    const handleOpenDeleteHieuChinhConfirm = (hieuChinh) => {
        setHieuChinhToDelete(hieuChinh);
        setShowDeleteHieuChinhConfirm(true);
    };

    const handleCloseDeleteHieuChinhConfirm = () => {
        setShowDeleteHieuChinhConfirm(false);
        setHieuChinhToDelete(null);
    };

    const handleDeleteHieuChinh = async () => {
        if (!hieuChinhToDelete) return;

        try {
            await authUtils.apiRequest('HIEU_CHINH_TIEN_NANG_SUAT', 'Delete', {
                Rows: [{ 'ID HIỆU CHỈNH': hieuChinhToDelete['ID HIỆU CHỈNH'] }]
            });
            toast.success('Xóa hiệu chỉnh thành công!');
            await fetchHieuChinhList();
            handleCloseDeleteHieuChinhConfirm();

            // Cập nhật lại history list nếu đang mở
            if (showHistoryModal && historyItem) {
                const updatedHistory = hieuChinhList.filter(
                    hc => hc['ID TỔ CÔNG ĐOẠN'] === historyItem['ID TỔ CÔNG ĐOẠN'] &&
                        hc['ID HIỆU CHỈNH'] !== hieuChinhToDelete['ID HIỆU CHỈNH']
                );
                setHistoryList(updatedHistory);
            }
        } catch (error) {
            console.error('Error deleting hieu chinh:', error);
            toast.error('Có lỗi xảy ra khi xóa hiệu chỉnh');
        }
    };

    // ===================== MODAL HANDLERS (PHÂN BỔ) =====================

    const handleOpenModal = (item = null) => {
        if (item) {
            setIsEditMode(true);
            setSelectedTos([]);
            setCurrentPhanBoNhanSu({
                'ID TỔ CÔNG ĐOẠN': item['ID TỔ CÔNG ĐOẠN'] || '',
                'TỔ': item['TỔ'] || '',
                'MÃ CÔNG ĐOẠN': item['MÃ CÔNG ĐOẠN'] || '',
                'TÊN CÔNG ĐOẠN': item['TÊN CÔNG ĐOẠN'] || '',
                'NHÂN SỰ PHỤ TRÁCH': item['NHÂN SỰ PHỤ TRÁCH'] || '',
                'SỐ HĐ PHỤ TRÁCH': item['SỐ HĐ PHỤ TRÁCH'] || '',
                'HIỆU LỰC TỪ': item['HIỆU LỰC TỪ'] || '',
                'HIỆU LỰC ĐẾN': item['HIỆU LỰC ĐẾN'] || '',
                'NHÓM': item['NHÓM'] || '',
                'ĐƠN VỊ TÍNH': item['ĐƠN VỊ TÍNH'] || '',
                'PP TÍNH NĂNG SUẤT': item['PP TÍNH NĂNG SUẤT'] || ''
            });
            setSelectedNhanSu(item['NHÂN SỰ PHỤ TRÁCH'] || '');
        } else {
            setIsEditMode(false);
            setSelectedTos([]);
            setCurrentPhanBoNhanSu(EMPTY_PHAN_BO);
            setSelectedNhanSu('');
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setIsEditMode(false);
        setSelectedNhanSu('');
        setSearchNhanSu('');
        setSearchTo('');
        setShowToDropdown(false);
        setSelectedTos([]);
        setCurrentPhanBoNhanSu(EMPTY_PHAN_BO);
    };

    // ===================== FORM HANDLERS =====================

    const handleInputChange = (field, value) => {
        setCurrentPhanBoNhanSu(prev => ({ ...prev, [field]: value }));
    };

    const handleCongDoanChange = (tenCongDoan) => {
        const selectedCongDoan = uniqueCongDoans.find(cd => cd['TÊN CÔNG ĐOẠN'] === tenCongDoan);
        setCurrentPhanBoNhanSu(prev => ({
            ...prev,
            'TÊN CÔNG ĐOẠN': tenCongDoan,
            'MÃ CÔNG ĐOẠN': selectedCongDoan ? selectedCongDoan['MÃ CÔNG ĐOẠN'] : '',
            'NHÓM': selectedCongDoan ? (selectedCongDoan['NHÓM NĂNG SUẤT'] || '') : '',
            'ĐƠN VỊ TÍNH': selectedCongDoan ? selectedCongDoan['ĐƠN VỊ TÍNH'] : '',
            'PP TÍNH NĂNG SUẤT': selectedCongDoan ? selectedCongDoan['PP TÍNH NĂNG SUẤT'] : ''
        }));
    };

    // handleToChange chỉ dùng khi edit (single tổ)
    const handleToChange = (to) => {
        setCurrentPhanBoNhanSu(prev => {
            const updated = { ...prev, 'TỔ': to };
            if (to && updated['MÃ CÔNG ĐOẠN']) {
                updated['ID TỔ CÔNG ĐOẠN'] = generateIdToCongDoan(to, updated['MÃ CÔNG ĐOẠN'], updated['NHÂN SỰ PHỤ TRÁCH']);
            }
            return updated;
        });
    };

    // Lưu cả SỐ HĐ: bảng lương gắn năng suất theo SỐ HĐ, tên chỉ để hiển thị và có thể trùng.
    const handleNhanSuChange = (tenNhanSu, soHd = '') => {
        setSelectedNhanSu(tenNhanSu);
        setCurrentPhanBoNhanSu(prev => {
            const updated = { ...prev, 'NHÂN SỰ PHỤ TRÁCH': tenNhanSu, 'SỐ HĐ PHỤ TRÁCH': soHd };
            if (updated['TỔ'] && updated['MÃ CÔNG ĐOẠN']) {
                updated['ID TỔ CÔNG ĐOẠN'] = generateIdToCongDoan(updated['TỔ'], updated['MÃ CÔNG ĐOẠN'], tenNhanSu);
            }
            return updated;
        });
    };

    const toggleToSelection = (to) => {
        setSelectedTos(prev =>
            prev.includes(to) ? prev.filter(t => t !== to) : [...prev, to]
        );
    };

    const selectNhanSu = (nhanSu) => {
        handleNhanSuChange(nhanSu['HỌ VÀ TÊN'], nhanSu['SỐ HĐ']);
    };

    const clearNhanSu = () => {
        handleNhanSuChange('');
    };

    // ===================== VALIDATION & SAVE =====================

    const validatePhanBoNhanSu = (item, tosToCheck = null) => {
        const errors = [];
        if (!tosToCheck && !item['TỔ']) errors.push('Chọn ít nhất một tổ');
        if (tosToCheck && tosToCheck.length === 0) errors.push('Chọn ít nhất một tổ');
        if (!item['TÊN CÔNG ĐOẠN']) errors.push('Tên công đoạn không được để trống');
        if (!item['NHÂN SỰ PHỤ TRÁCH'] || item['NHÂN SỰ PHỤ TRÁCH'].trim() === '')
            errors.push('Phải chọn nhân sự phụ trách');
        if (!item['HIỆU LỰC TỪ']) errors.push('Hiệu lực từ không được để trống');
        if (item['HIỆU LỰC ĐẾN'] && item['HIỆU LỰC TỪ']) {
            if (new Date(item['HIỆU LỰC ĐẾN']) < new Date(item['HIỆU LỰC TỪ'])) {
                errors.push('Hiệu lực đến phải sau hiệu lực từ');
            }
        }
        return errors;
    };

    // Tổ bên Nhân sự là bản sao của phân bổ: sửa phân bổ xong thì ghi lại ngay để hai bên không lệch.
    const syncTeams = async () => {
        try {
            const changes = await syncEmployeeTeams();
            if (changes.length) toast.info(`Đã cập nhật tổ cho ${changes.length} nhân sự theo phân bổ`);
        } catch (error) {
            console.error('Error syncing employee teams:', error);
            toast.warning('Chưa cập nhật được tổ bên Nhân sự, bấm "Đồng bộ tổ" ở màn Nhân sự để làm lại');
        }
    };

    const afterAllocationChange = async () => {
        await fetchPhanBoNhanSus();
        await syncTeams();
    };

    const handleSave = async () => {
        if (isSubmitting) return;

        try {
            setIsSubmitting(true);

            if (isEditMode) {
                // Edit: single row
                const errors = validatePhanBoNhanSu(currentPhanBoNhanSu);
                if (errors.length > 0) { toast.error(errors.join('\n')); return; }

                const dataToSave = {
                    'ID TỔ CÔNG ĐOẠN': currentPhanBoNhanSu['ID TỔ CÔNG ĐOẠN'],
                    'TỔ': currentPhanBoNhanSu['TỔ'],
                    'MÃ CÔNG ĐOẠN': currentPhanBoNhanSu['MÃ CÔNG ĐOẠN'],
                    'TÊN CÔNG ĐOẠN': currentPhanBoNhanSu['TÊN CÔNG ĐOẠN'],
                    'NHÂN SỰ PHỤ TRÁCH': currentPhanBoNhanSu['NHÂN SỰ PHỤ TRÁCH'],
                    'SỐ HĐ PHỤ TRÁCH': currentPhanBoNhanSu['SỐ HĐ PHỤ TRÁCH'],
                    'HIỆU LỰC TỪ': currentPhanBoNhanSu['HIỆU LỰC TỪ'],
                    'HIỆU LỰC ĐẾN': currentPhanBoNhanSu['HIỆU LỰC ĐẾN'],
                    'NHÓM': currentPhanBoNhanSu['NHÓM'],
                    'ĐƠN VỊ TÍNH': currentPhanBoNhanSu['ĐƠN VỊ TÍNH'],
                    'PP TÍNH NĂNG SUẤT': currentPhanBoNhanSu['PP TÍNH NĂNG SUẤT']
                };
                await authUtils.apiRequest('TO_PBNS', 'Edit', { Rows: [dataToSave] });
                toast.success('Cập nhật phân bổ nhân sự thành công!');
            } else {
                // Thêm mới: nhiều tổ → nhiều dòng
                const errors = validatePhanBoNhanSu(currentPhanBoNhanSu, selectedTos);
                if (errors.length > 0) { toast.error(errors.join('\n')); return; }

                // Build các dòng cần lưu
                const rowsToAdd = selectedTos.map(to => ({
                    'ID TỔ CÔNG ĐOẠN': generateIdToCongDoan(to, currentPhanBoNhanSu['MÃ CÔNG ĐOẠN'], currentPhanBoNhanSu['NHÂN SỰ PHỤ TRÁCH']),
                    'TỔ': to,
                    'MÃ CÔNG ĐOẠN': currentPhanBoNhanSu['MÃ CÔNG ĐOẠN'],
                    'TÊN CÔNG ĐOẠN': currentPhanBoNhanSu['TÊN CÔNG ĐOẠN'],
                    'NHÂN SỰ PHỤ TRÁCH': currentPhanBoNhanSu['NHÂN SỰ PHỤ TRÁCH'],
                    'SỐ HĐ PHỤ TRÁCH': currentPhanBoNhanSu['SỐ HĐ PHỤ TRÁCH'],
                    'HIỆU LỰC TỪ': currentPhanBoNhanSu['HIỆU LỰC TỪ'],
                    'HIỆU LỰC ĐẾN': currentPhanBoNhanSu['HIỆU LỰC ĐẾN'],
                    'NHÓM': currentPhanBoNhanSu['NHÓM'],
                    'ĐƠN VỊ TÍNH': currentPhanBoNhanSu['ĐƠN VỊ TÍNH'],
                    'PP TÍNH NĂNG SUẤT': currentPhanBoNhanSu['PP TÍNH NĂNG SUẤT']
                }));

                // Kiểm tra trùng ID
                const existingRecords = await authUtils.apiRequest('TO_PBNS', 'Find', {});
                const duplicates = rowsToAdd.filter(r =>
                    existingRecords.some(e => e['ID TỔ CÔNG ĐOẠN'] === r['ID TỔ CÔNG ĐOẠN'])
                );
                if (duplicates.length > 0) {
                    toast.error(`ID đã tồn tại: ${duplicates.map(d => d['ID TỔ CÔNG ĐOẠN']).join(', ')}`);
                    return;
                }

                await authUtils.apiRequest('TO_PBNS', 'Add', { Rows: rowsToAdd });
                toast.success(`Đã thêm ${rowsToAdd.length} phân bổ nhân sự thành công!`);
            }

            handleCloseModal();
            await afterAllocationChange();
        } catch (error) {
            console.error('Error saving phan bo nhan su:', error);
            toast.error('Có lỗi xảy ra: ' + (error.message || 'Không thể lưu phân bổ nhân sự'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // ===================== DELETE =====================

    const handleOpenDeleteConfirmation = (item) => {
        setItemToDelete(item);
        setShowDeleteConfirmation(true);
    };

    const handleCloseDeleteConfirmation = () => {
        setShowDeleteConfirmation(false);
        setItemToDelete(null);
    };

    const handleDelete = async () => {
        if (!itemToDelete) return;

        try {
            await authUtils.apiRequest('TO_PBNS', 'Delete', {
                Rows: [
                    {
                        'ID TỔ CÔNG ĐOẠN': itemToDelete['ID TỔ CÔNG ĐOẠN']
                    }
                ]
            });
            toast.success('Xóa phân bổ nhân sự thành công!');
            handleCloseDeleteConfirmation();
            await afterAllocationChange();
        } catch (error) {
            console.error('Error deleting phan bo nhan su:', error);
            toast.error('Có lỗi xảy ra khi xóa phân bổ nhân sự');
        }
    };

    // ===================== JSX =====================

    return (
        <div className="p-3 bg-gray-50 min-h-screen">
            <div className="mx-auto">
                <div className="bg-white rounded-xl shadow-sm p-5 mb-6 border border-gray-100">
                    {/* Header Section */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                        <h1 className="text-2xl font-bold text-gray-800">
                            Quản lý Phân Bổ Nhân Sự
                        </h1>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <Filter className="w-4 h-4" />
                                {showFilters ? 'Ẩn bộ lọc' : 'Bộ lọc'}
                            </button>
                            <button
                                onClick={() => {
                                    setExpandedNhoms(new Set(Object.keys(groupedData)));
                                    const allToKeys = [];
                                    Object.entries(groupedData).forEach(
                                        ([nhom, tosData]) => {
                                            Object.keys(tosData).forEach(to => {
                                                allToKeys.push(`${nhom}-${to}`);
                                            });
                                        }
                                    );
                                    setExpandedTos(new Set(allToKeys));
                                }}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                            >
                                Mở tất cả
                            </button>
                            <button
                                onClick={() => {
                                    setExpandedNhoms(new Set());
                                    setExpandedTos(new Set());
                                }}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                            >
                                Đóng tất cả
                            </button>
                            {isAdmin && (
                                <button
                                    onClick={() => setShowGhepSoHd(true)}
                                    className="px-4 py-2 text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 flex items-center gap-2 transition-colors shadow-sm"
                                    title="Gắn Số HĐ cho các dòng phân bổ chỉ ghi tên"
                                >
                                    <Link2 className="w-4 h-4" />
                                    Ghép Số HĐ
                                </button>
                            )}
                            <button
                                onClick={() => handleOpenModal()}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <Plus className="w-4 h-4" />
                                Thêm phân bổ
                            </button>
                        </div>
                    </div>

                    {/* Search & Filter */}
                    <div className="mb-6 space-y-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Tìm kiếm theo nhóm, tổ, công đoạn, nhân sự..."
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>

                        {showFilters && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg border">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Ngày tính đơn giá
                                    </label>
                                    <input
                                        type="date"
                                        value={ngayTinhDonGia}
                                        onChange={e => setNgayTinhDonGia(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Đơn giá và hiệu chỉnh sẽ được tính theo ngày này
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Lọc theo tổ
                                    </label>
                                    <select
                                        value={toFilter}
                                        onChange={e => setToFilter(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="">Tất cả tổ</option>
                                        {uniqueTos.map(to => (
                                            <option key={to} value={to}>
                                                {to}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex items-end">
                                    <button
                                        onClick={() => {
                                            setToFilter('');
                                            setSearch('');
                                            setNgayTinhDonGia(new Date().toISOString().split('T')[0]);
                                        }}
                                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        Xóa bộ lọc
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Info về ngày tính đơn giá */}
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center gap-2 text-blue-800">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm font-medium">
                                Đơn giá năng suất đang được tính theo ngày: <strong>{new Date(ngayTinhDonGia).toLocaleDateString('vi-VN')}</strong>
                            </span>
                        </div>
                    </div>

                    {/* Statistics */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                            <h3 className="text-sm text-blue-700 mb-1">Tổng phân bổ</h3>
                            <p className="text-2xl font-bold text-blue-800">
                                {phanBoNhanSus.length}
                            </p>
                        </div>

                        <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                            <h3 className="text-sm text-green-700 mb-1">Số nhóm</h3>
                            <p className="text-2xl font-bold text-green-800">
                                {Object.keys(groupedData).length}
                            </p>
                        </div>

                        <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
                            <h3 className="text-sm text-purple-700 mb-1">
                                Tổng nhân sự phụ trách
                            </h3>
                            <p className="text-2xl font-bold text-purple-800">
                                {
                                    new Set(
                                        phanBoNhanSus
                                            .map(item => item['NHÂN SỰ PHỤ TRÁCH'])
                                            .filter(ns => ns && ns.trim())
                                    ).size
                                }
                            </p>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
                            <h3 className="text-sm text-yellow-700 mb-1">
                                Đơn giá NS TB (sau HC)
                            </h3>
                            <p className="text-2xl font-bold text-yellow-800">
                                {phanBoWithDonGia.length > 0
                                    ? (
                                        phanBoWithDonGia.reduce(
                                            (sum, item) =>
                                                sum +
                                                (parseFloat(item['ĐƠN GIÁ SAU HIỆU CHỈNH']) || 0),
                                            0
                                        ) / phanBoWithDonGia.length
                                    ).toLocaleString('vi-VN', {
                                        maximumFractionDigits: 0
                                    })
                                    : 0}
                            </p>
                        </div>
                    </div>

                    {/* Grouped Table */}
                    <div className="overflow-x-auto">
                        {Object.keys(groupedData).length > 0 ? (
                            Object.entries(groupedData).map(
                                ([nhom, tosData]) => (
                                    <div key={nhom} className="mb-6">
                                        {/* Nhóm Header */}
                                        <div
                                            className="bg-indigo-50 border border-indigo-200 rounded-t-lg p-4 cursor-pointer hover:bg-indigo-100 transition-colors"
                                            onClick={() => toggleNhom(nhom)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    {expandedNhoms.has(nhom) ? (
                                                        <ChevronDown className="w-5 h-5 text-indigo-600" />
                                                    ) : (
                                                        <ChevronRight className="w-5 h-5 text-indigo-600" />
                                                    )}
                                                    <h3 className="text-lg font-semibold text-indigo-800">
                                                        Nhóm: {nhom}
                                                    </h3>
                                                    <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-sm font-medium">
                                                        {Object.keys(tosData).length} tổ
                                                    </span>
                                                </div>
                                                <div className="text-sm text-indigo-600">
                                                    Tổng phân bổ: {Object.values(tosData).flat().length}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Nhóm Content */}
                                        {expandedNhoms.has(nhom) && (
                                            <div className="border border-t-0 border-indigo-200 rounded-b-lg">
                                                {Object.entries(tosData).map(
                                                    ([to, items]) => (
                                                        <div
                                                            key={`${nhom}-${to}`}
                                                            className="border-b border-gray-100 last:border-b-0"
                                                        >
                                                            {/* Tổ Header */}
                                                            <div
                                                                className="bg-blue-50 border-b border-blue-100 p-3 cursor-pointer hover:bg-blue-100 transition-colors"
                                                                onClick={() => toggleTo(nhom, to)}
                                                            >
                                                                <div className="flex items-center justify-between">
                                                                    <div className="flex items-center gap-3">
                                                                        {expandedTos.has(`${nhom}-${to}`) ? (
                                                                            <ChevronDown className="w-4 h-4 text-blue-600" />
                                                                        ) : (
                                                                            <ChevronRight className="w-4 h-4 text-blue-600" />
                                                                        )}
                                                                        <h4 className="text-md font-semibold text-blue-800">
                                                                            Tổ: {to}
                                                                        </h4>
                                                                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                                                                            {items.length} công đoạn
                                                                        </span>
                                                                    </div>
                                                                    <div className="text-xs text-blue-600">
                                                                        Tổng phân bổ: {items.length}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Tổ Content - Table */}
                                                            {expandedTos.has(`${nhom}-${to}`) && (
                                                                <table className="min-w-full divide-y divide-gray-200">
                                                                    <thead className="bg-gray-50">
                                                                        <tr>
                                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                                Công đoạn
                                                                            </th>
                                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                                Nhân sự phụ trách
                                                                            </th>
                                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                                Hiệu lực
                                                                            </th>
                                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                                Đơn giá NS
                                                                            </th>
                                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                                Hiệu chỉnh
                                                                            </th>
                                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                                Đơn giá sau HC
                                                                            </th>
                                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                                ĐVT
                                                                            </th>
                                                                            <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                                                                                Thao tác
                                                                            </th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                                        {items.map((item, index) => (
                                                                            <tr
                                                                                key={`${item['ID TỔ CÔNG ĐOẠN']}-${index}`}
                                                                                className="hover:bg-gray-50 transition-colors"
                                                                            >
                                                                                                                                                                <td className="px-4 py-4 text-sm text-gray-900">
                                                                                    <div className="max-w-xs">
                                                                                        <div className="font-medium">
                                                                                            {item['TÊN CÔNG ĐOẠN']}
                                                                                        </div>
                                                                                        <div className="text-xs text-gray-500">
                                                                                            {item['MÃ CÔNG ĐOẠN']}
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                                <td className="px-4 py-4 text-sm text-gray-700">
                                                                                    <div className="max-w-xs">
                                                                                        <div
                                                                                            className="truncate"
                                                                                            title={item['NHÂN SỰ PHỤ TRÁCH']}
                                                                                        >
                                                                                            {item['NHÂN SỰ PHỤ TRÁCH']}
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                                                                                    <div className="flex flex-col gap-1">
                                                                                        {item['HIỆU LỰC TỪ'] ? (
                                                                                            <>
                                                                                                <span className="text-xs text-gray-600">
                                                                                                    {new Date(item['HIỆU LỰC TỪ']).toLocaleDateString('vi-VN')}
                                                                                                    {' — '}
                                                                                                    {item['HIỆU LỰC ĐẾN']
                                                                                                        ? new Date(item['HIỆU LỰC ĐẾN']).toLocaleDateString('vi-VN')
                                                                                                        : '∞'}
                                                                                                </span>
                                                                                                {item['IS_PB_ACTIVE'] ? (
                                                                                                    <span className="inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700 w-fit">
                                                                                                        Đang HLực
                                                                                                    </span>
                                                                                                ) : (
                                                                                                    <span className="inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-500 w-fit">
                                                                                                        Hết HLực
                                                                                                    </span>
                                                                                                )}
                                                                                            </>
                                                                                        ) : (
                                                                                            <span className="text-gray-400">—</span>
                                                                                        )}
                                                                                    </div>
                                                                                </td>
                                                                                <td className="px-4 py-4 whitespace-nowrap text-sm">
                                                                                    <div className="flex flex-col">
                                                                                        <span className={`font-semibold ${item['IS_OUTDATED'] ? 'text-orange-600' : 'text-gray-900'}`}>
                                                                                            {parseFloat(item['ĐƠN GIÁ NĂNG SUẤT'] || 0).toLocaleString('vi-VN')}
                                                                                        </span>
                                                                                        {item['HIỆU LỰC TỪ_DONGIA'] && (
                                                                                            <span className="text-xs text-gray-400">
                                                                                                {new Date(item['HIỆU LỰC TỪ_DONGIA']).toLocaleDateString('vi-VN')}
                                                                                                {item['HIỆU LỰC ĐẾN_DONGIA'] && (
                                                                                                    <> - {new Date(item['HIỆU LỰC ĐẾN_DONGIA']).toLocaleDateString('vi-VN')}</>
                                                                                                )}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                </td>
                                                                                <td className="px-4 py-4 whitespace-nowrap text-sm">
                                                                                    {item['CÓ HIỆU CHỈNH'] ? (
                                                                                        <div className="flex flex-col">
                                                                                            <span className={`font-semibold ${item['TIỀN HIỆU CHỈNH'] >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                                                                {item['TIỀN HIỆU CHỈNH'] >= 0 ? '+' : ''}
                                                                                                {parseFloat(item['TIỀN HIỆU CHỈNH']).toLocaleString('vi-VN')}
                                                                                            </span>
                                                                                            {item['HIỆU LỰC TỪ_HC'] && (
                                                                                                <span className="text-xs text-gray-400">
                                                                                                    {new Date(item['HIỆU LỰC TỪ_HC']).toLocaleDateString('vi-VN')}
                                                                                                    {item['HIỆU LỰC ĐẾN_HC'] && (
                                                                                                        <> - {new Date(item['HIỆU LỰC ĐẾN_HC']).toLocaleDateString('vi-VN')}</>
                                                                                                    )}
                                                                                                </span>
                                                                                            )}
                                                                                            {item['GHI CHÚ_HC'] && (
                                                                                                <span className="text-xs text-gray-500 italic truncate max-w-[100px]" title={item['GHI CHÚ_HC']}>
                                                                                                    {item['GHI CHÚ_HC']}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    ) : (
                                                                                        <span className="text-gray-400">—</span>
                                                                                    )}
                                                                                </td>
                                                                                <td className="px-4 py-4 whitespace-nowrap text-sm">
                                                                                    <span className={`font-bold text-lg ${item['CÓ HIỆU CHỈNH'] ? 'text-indigo-600' : 'text-gray-900'}`}>
                                                                                        {parseFloat(item['ĐƠN GIÁ SAU HIỆU CHỈNH'] || 0).toLocaleString('vi-VN')}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                                                                                    {item['ĐƠN VỊ TÍNH']}
                                                                                </td>
                                                                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                                                                    <div className="flex justify-center space-x-1">
                                                                                        {/* Nút hiệu chỉnh */}
                                                                                        <button
                                                                                            onClick={() => handleOpenHieuChinhModal(item)}
                                                                                            className="text-green-600 hover:text-green-900 p-1.5 rounded-full hover:bg-green-50"
                                                                                            title="Thêm hiệu chỉnh"
                                                                                        >
                                                                                            <DollarSign className="h-4 w-4" />
                                                                                        </button>
                                                                                        {/* Nút xem lịch sử hiệu chỉnh */}
                                                                                        <button
                                                                                            onClick={() => handleOpenHistoryModal(item)}
                                                                                            className="text-blue-600 hover:text-blue-900 p-1.5 rounded-full hover:bg-blue-50"
                                                                                            title="Xem lịch sử hiệu chỉnh"
                                                                                        >
                                                                                            <History className="h-4 w-4" />
                                                                                        </button>
                                                                                        {/* Nút sửa */}
                                                                                        <button
                                                                                            onClick={() => handleOpenModal(item)}
                                                                                            className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded-full hover:bg-indigo-50"
                                                                                            title="Sửa phân bổ"
                                                                                        >
                                                                                            <Edit className="h-4 w-4" />
                                                                                        </button>
                                                                                        {/* Nút xóa */}
                                                                                        <button
                                                                                            onClick={() => handleOpenDeleteConfirmation(item)}
                                                                                            className="text-red-600 hover:text-red-900 p-1.5 rounded-full hover:bg-red-50"
                                                                                            title="Xóa phân bổ"
                                                                                        >
                                                                                            <Trash className="h-4 w-4" />
                                                                                        </button>
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            )}
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )
                            )
                        ) : (
                            <div className="text-center py-12 bg-white rounded-lg border">
                                <div className="text-gray-500 text-lg mb-2">
                                    Không tìm thấy dữ liệu
                                </div>
                                <div className="text-sm text-gray-400">
                                    Không có phân bổ nhân sự nào phù hợp với tiêu chí tìm kiếm
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ==================== MODAL THÊM HIỆU CHỈNH ==================== */}
            {showHieuChinhModal && selectedItemForHieuChinh && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-5">
                            <h2 className="text-xl font-bold text-gray-800">
                                {isEditHieuChinh ? 'Cập nhật hiệu chỉnh tiền năng suất' : 'Thêm hiệu chỉnh tiền năng suất'}
                            </h2>
                            <button
                                onClick={handleCloseHieuChinhModal}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Thông tin tổ công đoạn */}
                        <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                            <h3 className="text-sm font-semibold text-indigo-800 mb-2">Thông tin Tổ Công Đoạn</h3>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                <div>
                                    <span className="text-gray-600">ID:</span>{' '}
                                    <span className="font-medium">{selectedItemForHieuChinh['ID TỔ CÔNG ĐOẠN']}</span>
                                </div>
                                <div>
                                    <span className="text-gray-600">Tổ:</span>{' '}
                                    <span className="font-medium">{selectedItemForHieuChinh['TỔ']}</span>
                                </div>
                                <div>
                                    <span className="text-gray-600">Công đoạn:</span>{' '}
                                    <span className="font-medium">{selectedItemForHieuChinh['TÊN CÔNG ĐOẠN']}</span>
                                </div>
                                <div>
                                    <span className="text-gray-600">Nhân sự:</span>{' '}
                                    <span className="font-medium">{selectedItemForHieuChinh['NHÂN SỰ PHỤ TRÁCH']}</span>
                                </div>
                                <div>
                                    <span className="text-gray-600">Đơn giá gốc:</span>{' '}
                                    <span className="font-bold text-indigo-600">
                                        {parseFloat(selectedItemForHieuChinh['ĐƠN GIÁ NĂNG SUẤT'] || 0).toLocaleString('vi-VN')} VNĐ
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Ngày hiệu chỉnh <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={formatDateForInput(currentHieuChinh['NGÀY HIỆU CHỈNH'])}
                                        onChange={e => handleHieuChinhInputChange('NGÀY HIỆU CHỈNH', e.target.value)}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Tiền hiệu chỉnh <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={currentHieuChinh['TIỀN HIỆU CHỈNH']}
                                        onChange={e => handleHieuChinhInputChange('TIỀN HIỆU CHỈNH', e.target.value)}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Nhập số dương (+) hoặc âm (-)"
                                        required
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Nhập số dương để tăng, số âm để giảm đơn giá
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Hiệu lực từ <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={formatDateForInput(currentHieuChinh['HIỆU LỰC TỪ'])}
                                        onChange={e => handleHieuChinhInputChange('HIỆU LỰC TỪ', e.target.value)}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Hiệu lực đến
                                    </label>
                                    <input
                                        type="date"
                                        value={formatDateForInput(currentHieuChinh['HIỆU LỰC ĐẾN'])}
                                        onChange={e => handleHieuChinhInputChange('HIỆU LỰC ĐẾN', e.target.value)}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Để trống nếu không có ngày kết thúc
                                    </p>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    ID Hiệu chỉnh (tự động tạo)
                                </label>
                                <input
                                    type="text"
                                    value={currentHieuChinh['ID HIỆU CHỈNH']}
                                    className="p-2.5 border border-gray-300 rounded-lg w-full bg-gray-100"
                                    readOnly
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Ghi chú
                                </label>
                                <textarea
                                    value={currentHieuChinh['GHI CHÚ']}
                                    onChange={e => handleHieuChinhInputChange('GHI CHÚ', e.target.value)}
                                    className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                    rows={3}
                                    placeholder="Nhập lý do hiệu chỉnh..."
                                />
                            </div>

                            {/* Preview đơn giá sau hiệu chỉnh */}
                            {currentHieuChinh['TIỀN HIỆU CHỈNH'] && (
                                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                    <h4 className="text-sm font-semibold text-green-800 mb-2">Xem trước đơn giá sau hiệu chỉnh</h4>
                                    <div className="flex items-center gap-4">
                                        <div>
                                            <span className="text-gray-600 text-sm">Đơn giá gốc:</span>
                                            <span className="ml-2 font-semibold">
                                                {parseFloat(selectedItemForHieuChinh['ĐƠN GIÁ NĂNG SUẤT'] || 0).toLocaleString('vi-VN')}
                                            </span>
                                        </div>
                                        <div className={`font-bold ${parseFloat(currentHieuChinh['TIỀN HIỆU CHỈNH']) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {parseFloat(currentHieuChinh['TIỀN HIỆU CHỈNH']) >= 0 ? '+' : ''}
                                            {parseFloat(currentHieuChinh['TIỀN HIỆU CHỈNH']).toLocaleString('vi-VN')}
                                        </div>
                                        <div className="text-xl font-bold text-indigo-600">
                                            = {(
                                                (parseFloat(selectedItemForHieuChinh['ĐƠN GIÁ NĂNG SUẤT']) || 0) +
                                                (parseFloat(currentHieuChinh['TIỀN HIỆU CHỈNH']) || 0)
                                            ).toLocaleString('vi-VN')} VNĐ
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-6">
                            <button
                                onClick={handleCloseHieuChinhModal}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                                disabled={isSubmitting}
                            >
                                Hủy
                            </button>
                            <button
                                onClick={handleSaveHieuChinh}
                                disabled={isSubmitting}
                                className={`px-4 py-2 bg-green-600 text-white rounded-lg ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-700'
                                    } flex items-center gap-2 transition-colors shadow-sm`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Đang lưu...
                                    </>
                                ) : (
                                    <>
                                        <Check className="w-4 h-4" />
                                        {isEditHieuChinh ? 'Cập nhật' : 'Thêm hiệu chỉnh'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ==================== MODAL LỊCH SỬ HIỆU CHỈNH ==================== */}
            {showGhepSoHd && (
                <GhepSoHdModal
                    allocations={phanBoNhanSus}
                    employees={danhSachNhanSu}
                    onClose={() => setShowGhepSoHd(false)}
                    onSaved={afterAllocationChange}
                />
            )}

            {showHistoryModal && historyItem && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-5">
                            <h2 className="text-xl font-bold text-gray-800">
                                Lịch sử hiệu chỉnh tiền năng suất
                            </h2>
                            <button
                                onClick={handleCloseHistoryModal}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Thông tin tổ công đoạn */}
                        <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                            <h3 className="text-sm font-semibold text-indigo-800 mb-2">Thông tin Tổ Công Đoạn</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                                <div>
                                    <span className="text-gray-600">ID:</span>{' '}
                                    <span className="font-medium">{historyItem['ID TỔ CÔNG ĐOẠN']}</span>
                                </div>
                                <div>
                                    <span className="text-gray-600">Tổ:</span>{' '}
                                    <span className="font-medium">{historyItem['TỔ']}</span>
                                </div>
                                <div>
                                    <span className="text-gray-600">Công đoạn:</span>{' '}
                                    <span className="font-medium">{historyItem['TÊN CÔNG ĐOẠN']}</span>
                                </div>
                                <div>
                                    <span className="text-gray-600">Đơn giá gốc hiện tại:</span>{' '}
                                    <span className="font-bold text-indigo-600">
                                        {parseFloat(historyItem['ĐƠN GIÁ NĂNG SUẤT'] || 0).toLocaleString('vi-VN')}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Nút thêm hiệu chỉnh mới */}
                        <div className="mb-4 flex justify-end">
                            <button
                                onClick={() => {
                                    handleCloseHistoryModal();
                                    handleOpenHieuChinhModal(historyItem);
                                }}
                                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <Plus className="w-4 h-4" />
                                Thêm hiệu chỉnh mới
                            </button>
                        </div>

                        {/* Bảng lịch sử */}
                        {historyList.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Ngày HC
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Hiệu lực
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Tiền hiệu chỉnh
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Ghi chú
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Trạng thái
                                            </th>
                                            <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                                                Thao tác
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {historyList.map((hc, index) => {
                                            const today = new Date();
                                            const hieuLucTu = hc['HIỆU LỰC TỪ'] ? new Date(hc['HIỆU LỰC TỪ']) : null;
                                            const hieuLucDen = hc['HIỆU LỰC ĐẾN'] ? new Date(hc['HIỆU LỰC ĐẾN']) : null;

                                            const isActive = (!hieuLucTu || today >= hieuLucTu) &&
                                                (!hieuLucDen || today <= hieuLucDen);

                                            return (
                                                <tr key={hc['ID HIỆU CHỈNH'] || index} className={`hover:bg-gray-50 ${isActive ? 'bg-green-50' : ''}`}>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                        {hc['NGÀY HIỆU CHỈNH']
                                                            ? new Date(hc['NGÀY HIỆU CHỈNH']).toLocaleDateString('vi-VN')
                                                            : '—'}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                        <div>
                                                            {hc['HIỆU LỰC TỪ']
                                                                ? new Date(hc['HIỆU LỰC TỪ']).toLocaleDateString('vi-VN')
                                                                : '—'}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            đến {hc['HIỆU LỰC ĐẾN']
                                                                ? new Date(hc['HIỆU LỰC ĐẾN']).toLocaleDateString('vi-VN')
                                                                : '∞'}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                        <span className={`font-bold ${parseFloat(hc['TIỀN HIỆU CHỈNH']) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                            {parseFloat(hc['TIỀN HIỆU CHỈNH']) >= 0 ? '+' : ''}
                                                            {parseFloat(hc['TIỀN HIỆU CHỈNH']).toLocaleString('vi-VN')}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">
                                                        <div className="max-w-xs truncate" title={hc['GHI CHÚ']}>
                                                            {hc['GHI CHÚ'] || '—'}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                        {isActive ? (
                                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                                                Đang hiệu lực
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                                                                Hết hiệu lực
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                                                        <div className="flex justify-center space-x-2">
                                                            <button
                                                                onClick={() => {
                                                                    handleCloseHistoryModal();
                                                                    handleOpenHieuChinhModal(historyItem, hc);
                                                                }}
                                                                className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded-full hover:bg-indigo-50"
                                                                title="Sửa hiệu chỉnh"
                                                            >
                                                                <Edit className="h-4 w-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleOpenDeleteHieuChinhConfirm(hc)}
                                                                className="text-red-600 hover:text-red-900 p-1.5 rounded-full hover:bg-red-50"
                                                                title="Xóa hiệu chỉnh"
                                                            >
                                                                <Trash className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-8 bg-gray-50 rounded-lg">
                                <div className="text-gray-500 mb-2">Chưa có lịch sử hiệu chỉnh</div>
                                <div className="text-sm text-gray-400">
                                    Nhấn "Thêm hiệu chỉnh mới" để tạo hiệu chỉnh đầu tiên
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
                            <button
                                onClick={handleCloseHistoryModal}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                            >
                                Đóng
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ==================== MODAL XÁC NHẬN XÓA HIỆU CHỈNH ==================== */}
            {showDeleteHieuChinhConfirm && hieuChinhToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-5">
                            <h2 className="text-xl font-bold text-gray-800">
                                Xác nhận xóa hiệu chỉnh
                            </h2>
                            <button
                                onClick={handleCloseDeleteHieuChinhConfirm}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="space-y-5">
                            <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                                <p className="text-red-700">
                                    Bạn có chắc chắn muốn xóa hiệu chỉnh này?
                                </p>
                                <div className="mt-3 text-sm text-red-600">
                                    <p>
                                        <strong>ID:</strong> {hieuChinhToDelete['ID HIỆU CHỈNH']}
                                    </p>
                                    <p>
                                        <strong>Tiền hiệu chỉnh:</strong>{' '}
                                        <span className={parseFloat(hieuChinhToDelete['TIỀN HIỆU CHỈNH']) >= 0 ? 'text-green-600' : 'text-red-600'}>
                                            {parseFloat(hieuChinhToDelete['TIỀN HIỆU CHỈNH']) >= 0 ? '+' : ''}
                                            {parseFloat(hieuChinhToDelete['TIỀN HIỆU CHỈNH']).toLocaleString('vi-VN')}
                                        </span>
                                    </p>
                                    <p>
                                        <strong>Hiệu lực:</strong>{' '}
                                        {hieuChinhToDelete['HIỆU LỰC TỪ']
                                            ? new Date(hieuChinhToDelete['HIỆU LỰC TỪ']).toLocaleDateString('vi-VN')
                                            : '—'}{' '}
                                        đến{' '}
                                        {hieuChinhToDelete['HIỆU LỰC ĐẾN']
                                            ? new Date(hieuChinhToDelete['HIỆU LỰC ĐẾN']).toLocaleDateString('vi-VN')
                                            : '∞'}
                                    </p>
                                    {hieuChinhToDelete['GHI CHÚ'] && (
                                        <p>
                                            <strong>Ghi chú:</strong> {hieuChinhToDelete['GHI CHÚ']}
                                        </p>
                                    )}
                                </div>
                                <p className="text-sm text-red-600 mt-2">
                                    Hành động này không thể hoàn tác.
                                </p>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    onClick={handleCloseDeleteHieuChinhConfirm}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                                >
                                    Hủy
                                </button>
                                <button
                                    onClick={handleDeleteHieuChinh}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 transition-colors shadow-sm"
                                >
                                    <Trash className="h-4 w-4" />
                                    Xóa hiệu chỉnh
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ==================== MODAL THÊM/SỬA PHÂN BỔ ==================== */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-5">
                            <h2 className="text-xl font-bold text-gray-800">
                                {isEditMode ? 'Cập nhật phân bổ nhân sự' : 'Thêm phân bổ nhân sự mới'}
                            </h2>
                            <button
                                onClick={handleCloseModal}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Left Column */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {isEditMode ? 'Tổ' : 'Chọn tổ'} <span className="text-red-500">*</span>
                                        {!isEditMode && selectedTos.length > 0 && (
                                            <span className="ml-2 text-xs font-normal text-indigo-600">
                                                Đã chọn: {selectedTos.length} tổ
                                            </span>
                                        )}
                                    </label>
                                    {isEditMode ? (
                                        // Edit mode: single tổ (input)
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={currentPhanBoNhanSu['TỔ']}
                                                onChange={e => handleToChange(e.target.value)}
                                                className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                placeholder="Tên tổ"
                                            />
                                        </div>
                                    ) : (
                                        // Add mode: multi-select checkbox
                                        <div>
                                            <div className="relative mb-1.5">
                                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                                <input
                                                    type="text"
                                                    placeholder="Tìm tổ..."
                                                    value={searchTo}
                                                    onChange={e => setSearchTo(e.target.value)}
                                                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                />
                                            </div>
                                            <div className="border border-gray-300 rounded-lg max-h-44 overflow-y-auto">
                                                {/* Chọn tất cả */}
                                                {!searchTo && (
                                                    <div
                                                        onClick={() => setSelectedTos(
                                                            selectedTos.length === uniqueTos.length ? [] : [...uniqueTos]
                                                        )}
                                                        className="flex items-center px-3 py-2 cursor-pointer border-b border-gray-100 hover:bg-gray-50"
                                                    >
                                                        <div className={`w-4 h-4 rounded border-2 mr-2.5 flex-shrink-0 flex items-center justify-center ${selectedTos.length === uniqueTos.length ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}>
                                                            {selectedTos.length === uniqueTos.length && <Check className="w-2.5 h-2.5 text-white" />}
                                                        </div>
                                                        <span className="text-sm font-medium text-gray-600">Chọn tất cả</span>
                                                    </div>
                                                )}
                                                {uniqueTos
                                                    .sort()
                                                    .filter(to => !searchTo || to.toLowerCase().includes(searchTo.toLowerCase()))
                                                    .map(to => (
                                                        <div
                                                            key={to}
                                                            onClick={() => toggleToSelection(to)}
                                                            className={`flex items-center px-3 py-2 cursor-pointer border-b border-gray-50 last:border-0 transition-colors ${selectedTos.includes(to) ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}
                                                        >
                                                            <div className={`w-4 h-4 rounded border-2 mr-2.5 flex-shrink-0 flex items-center justify-center ${selectedTos.includes(to) ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}>
                                                                {selectedTos.includes(to) && <Check className="w-2.5 h-2.5 text-white" />}
                                                            </div>
                                                            <span className={`text-sm ${selectedTos.includes(to) ? 'font-semibold text-indigo-800' : 'text-gray-700'}`}>{to}</span>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                            {selectedTos.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-1.5">
                                                    {selectedTos.map(to => (
                                                        <span key={to} className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                                                            {to}
                                                            <button type="button" onClick={() => toggleToSelection(to)} className="hover:text-indigo-900">
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Tên công đoạn <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={currentPhanBoNhanSu['TÊN CÔNG ĐOẠN']}
                                        onChange={e => handleCongDoanChange(e.target.value)}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        required
                                    >
                                        <option value="">Chọn công đoạn</option>
                                        {uniqueCongDoans.map(cd => (
                                            <option key={cd['MÃ CÔNG ĐOẠN']} value={cd['TÊN CÔNG ĐOẠN']}>
                                                {cd['TÊN CÔNG ĐOẠN']}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Hiệu lực từ <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            value={formatDateForInput(currentPhanBoNhanSu['HIỆU LỰC TỪ'])}
                                            onChange={e => handleInputChange('HIỆU LỰC TỪ', e.target.value)}
                                            className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Hiệu lực đến
                                        </label>
                                        <input
                                            type="date"
                                            value={formatDateForInput(currentPhanBoNhanSu['HIỆU LỰC ĐẾN'])}
                                            onChange={e => handleInputChange('HIỆU LỰC ĐẾN', e.target.value)}
                                            className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Để trống nếu không giới hạn</p>
                                    </div>
                                </div>

                                {isEditMode && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            ID Tổ Công Đoạn (tự tạo)
                                        </label>
                                        <input
                                            type="text"
                                            value={currentPhanBoNhanSu['ID TỔ CÔNG ĐOẠN']}
                                            className="p-2.5 border border-gray-300 rounded-lg w-full bg-gray-100"
                                            readOnly
                                        />
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Mã công đoạn
                                        </label>
                                        <input
                                            type="text"
                                            value={currentPhanBoNhanSu['MÃ CÔNG ĐOẠN']}
                                            className="p-2.5 border border-gray-300 rounded-lg w-full bg-gray-100"
                                            readOnly
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Nhóm
                                        </label>
                                        <input
                                            type="text"
                                            value={currentPhanBoNhanSu['NHÓM']}
                                            className="p-2.5 border border-gray-300 rounded-lg w-full bg-gray-100"
                                            readOnly
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Đơn vị tính
                                        </label>
                                        <input
                                            type="text"
                                            value={currentPhanBoNhanSu['ĐƠN VỊ TÍNH']}
                                            className="p-2.5 border border-gray-300 rounded-lg w-full bg-gray-100"
                                            readOnly
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            PP tính năng suất
                                        </label>
                                        <input
                                            type="text"
                                            value={currentPhanBoNhanSu['PP TÍNH NĂNG SUẤT']}
                                            className="p-2.5 border border-gray-300 rounded-lg w-full bg-gray-100"
                                            readOnly
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Nhân sự selection */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Chọn nhân sự phụ trách <span className="text-red-500">*</span>
                                    </label>

                                    <div className="relative mb-2">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Tìm kiếm nhân viên..."
                                            value={searchNhanSu}
                                            onChange={e => setSearchNhanSu(e.target.value)}
                                            className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                        {searchNhanSu && (
                                            <button type="button" onClick={() => setSearchNhanSu('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                                <X className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>

                                    <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                                        {filteredNhanSu.length > 0 ? (
                                            filteredNhanSu.map((nv, idx) => {
                                                const isSelected = currentPhanBoNhanSu['SỐ HĐ PHỤ TRÁCH']
                                                    ? currentPhanBoNhanSu['SỐ HĐ PHỤ TRÁCH'] === nv['SỐ HĐ']
                                                    : selectedNhanSu === nv['HỌ VÀ TÊN'];
                                                return (
                                                    <div
                                                        key={idx}
                                                        onClick={() => selectNhanSu(nv)}
                                                        className={`flex items-center px-3 py-2 cursor-pointer transition-colors border-b border-gray-50 last:border-0 ${isSelected ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}
                                                    >
                                                        <div className={`w-4 h-4 rounded-full border-2 mr-3 flex-shrink-0 flex items-center justify-center ${isSelected ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'}`}>
                                                            {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                                        </div>
                                                        <span className={`text-sm ${isSelected ? 'font-semibold text-indigo-800' : 'text-gray-700'}`}>
                                                            {nv['HỌ VÀ TÊN']}
                                                        </span>
                                                        <span className="ml-auto text-xs text-gray-400">{nv['SỐ HĐ']}</span>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="text-center py-6 text-gray-400 text-sm">
                                                {searchNhanSu ? `Không tìm thấy "${searchNhanSu}"` : 'Không có nhân viên'}
                                            </div>
                                        )}
                                    </div>

                                    {selectedNhanSu && (
                                        <div className="mt-1.5">
                                            <button
                                                type="button"
                                                onClick={clearNhanSu}
                                                className="px-3 py-1 text-xs bg-red-50 text-red-600 rounded-full hover:bg-red-100 border border-red-200"
                                            >
                                                Bỏ chọn
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {selectedNhanSu && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Nhân sự phụ trách đã chọn
                                        </label>
                                        <div className="border border-indigo-300 rounded-lg p-3 bg-indigo-50">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-semibold text-indigo-900">
                                                    {selectedNhanSu}
                                                    <span className={`ml-2 text-xs font-normal ${currentPhanBoNhanSu['SỐ HĐ PHỤ TRÁCH'] ? 'text-indigo-600' : 'text-orange-600'}`}>
                                                        {currentPhanBoNhanSu['SỐ HĐ PHỤ TRÁCH']
                                                            ? `Số HĐ: ${currentPhanBoNhanSu['SỐ HĐ PHỤ TRÁCH']}`
                                                            : 'Chưa gắn Số HĐ, năng suất sẽ không tính vào lương'}
                                                    </span>
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={clearNhanSu}
                                                    className="text-indigo-600 hover:text-indigo-800"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-6">
                            <button
                                onClick={handleCloseModal}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                disabled={isSubmitting}
                            >
                                Hủy
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSubmitting}
                                className={`px-4 py-2 bg-indigo-600 text-white rounded-lg ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700'
                                    } flex items-center gap-2`}
                            >
                                {isSubmitting ? 'Đang lưu...' : (
                                    <>
                                        <Check className="w-4 h-4" />
                                        {isEditMode ? 'Cập nhật' : 'Thêm mới'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ==================== MODAL XÁC NHẬN XÓA PHÂN BỔ ==================== */}
            {showDeleteConfirmation && itemToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-5">
                            <h2 className="text-xl font-bold text-gray-800">Xác nhận xóa</h2>
                            <button onClick={handleCloseDeleteConfirmation} className="text-gray-500 hover:text-gray-700">
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-5">
                            <p className="text-red-700">Bạn có chắc chắn muốn xóa phân bổ nhân sự này?</p>
                            <div className="mt-3 text-sm text-red-600">
                                <p><strong>ID:</strong> {itemToDelete['ID TỔ CÔNG ĐOẠN']}</p>
                                <p><strong>Tổ:</strong> {itemToDelete['TỔ']}</p>
                                <p><strong>Công đoạn:</strong> {itemToDelete['TÊN CÔNG ĐOẠN']}</p>
                                <p><strong>Nhân sự:</strong> {itemToDelete['NHÂN SỰ PHỤ TRÁCH']}</p>
                            </div>
                            <p className="text-sm text-red-600 mt-2">Hành động này không thể hoàn tác.</p>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={handleCloseDeleteConfirmation}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Hủy
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                            >
                                <Trash className="h-4 w-4" />
                                Xóa phân bổ
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
};

export default PhanBoNhanSuManagement;

